// components/PromoCode/PromoCodeModal.js
import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Spinner } from "reactstrap";

const PromoCodeModal = ({ isOpen, toggle, promoCode, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    codename: "",
    codemoney: "",
    freedelivery: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with promo code data if editing
  useEffect(() => {
    if (promoCode) {
      setFormData({
        name: promoCode.name || "",
        codename: promoCode.codename || "",
        codemoney: promoCode.codemoney || "",
        freedelivery: promoCode.freedelivery || false,
      });
    } else {
      setFormData({
        name: "",
        codename: "",
        codemoney: "",
        freedelivery: false,
      });
    }
    setErrors({});
  }, [promoCode, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Promo name is required";
    }

    if (!formData.codename.trim()) {
      newErrors.codename = "Promo code is required";
    } else if (formData.codename.length < 3) {
      newErrors.codename = "Promo code must be at least 3 characters";
    }

    if (!formData.freedelivery && !formData.codemoney) {
      newErrors.codemoney = "Discount amount is required for non-free delivery promos";
    } else if (formData.codemoney && (isNaN(formData.codemoney) || parseFloat(formData.codemoney) < 0)) {
      newErrors.codemoney = "Discount amount must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error("Failed to save promo code:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({
      name: "",
      codename: "",
      codemoney: "",
      freedelivery: false,
    });
    setErrors({});
    toggle();
  };

  const title = promoCode ? "Edit Promo Code" : "Create New Promo Code";
  const submitText = promoCode ? "Update Promo Code" : "Create Promo Code";

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose}>
      <ModalHeader toggle={handleModalClose}>{title}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="name">Promo Name *</Label>
          <input
            type="text"
            id="name"
            name="name"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Summer Sale, Free Delivery"
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </FormGroup>

        <FormGroup>
          <Label for="codename">Promo Code *</Label>
          <input
            type="text"
            id="codename"
            name="codename"
            className={`form-control ${errors.codename ? 'is-invalid' : ''}`}
            value={formData.codename}
            onChange={handleChange}
            placeholder="e.g., SUMMER2024, FREEDEL"
          />
          {errors.codename && <div className="invalid-feedback">{errors.codename}</div>}
          {/* <small className="form-text text-muted">
            Customers will enter this code at checkout
          </small> */}
        </FormGroup>

        <FormGroup check className="mb-3">
          <input
            type="checkbox"
            id="freedelivery"
            name="freedelivery"
            checked={formData.freedelivery}
            onChange={handleChange}
            className="form-check-input"
          />
          <Label for="freedelivery" check className="form-check-label">
            Free Delivery
          </Label>
          <small className="form-text text-muted d-block">
            If checked, this promo will provide free delivery instead of discount
          </small>
        </FormGroup>

        <FormGroup>
          <Label for="codemoney">
            Discount Amount {!formData.freedelivery && '*'}
          </Label>
          <div className="input-group">
            <span className="input-group-text">৳</span>
            <input
              type="number"
              id="codemoney"
              name="codemoney"
              className={`form-control ${errors.codemoney ? 'is-invalid' : ''}`}
              value={formData.codemoney}
              onChange={handleChange}
              disabled={formData.freedelivery}
              placeholder="Discount amount"
              min="0"
              step="0.01"
            />
          </div>
          {errors.codemoney && <div className="invalid-feedback">{errors.codemoney}</div>}
          <small className="form-text text-muted">
            {formData.freedelivery
              ? "Free delivery promo - discount amount will be ignored"
              : "Enter discount amount in Bangladeshi Taka"}
          </small>
        </FormGroup>

        {/* <div className="alert alert-info mt-3">
          <i className="fas fa-info-circle me-2"></i>
          <strong>Note:</strong> Promo codes are case-sensitive. Make sure to inform customers exactly how to enter the code.
        </div> */}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleModalClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : submitText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PromoCodeModal;